import { IonImg } from '@ionic/react';
import { MouseEventHandler, PropsWithChildren, useEffect } from 'react';

import { useActiveSessionStore } from '@/hooks/useActiveSessionStore';
import { useSensors } from '@/hooks/useSensors';

import { useSensorsContext } from '@/contexts/SensorsContext';

import ErrorIllustration from '@/assets/illustrations/ErrorIllustration.svg';

import styles from './RecordingCancelled.module.css';

import { Button, ButtonProps } from '../Button';

type ButtonWithChildrenProps = PropsWithChildren<ButtonProps>;

interface RecordingCancelledProps {
  title?: string;
  description?: string;
  endSessionButton?: ButtonWithChildrenProps;
  newRecordingButton?: ButtonWithChildrenProps;
  reconfigureSensorsButton?: ButtonWithChildrenProps;
  isHubFlow: boolean;
}

export const RecordingCancelled = ({
  title = 'Recording cancelled',
  description = 'Connection to sensor was lost while recording, re-configure sensors if not automatically recovered.',
  endSessionButton,
  newRecordingButton,
  reconfigureSensorsButton,
  isHubFlow,
}: RecordingCancelledProps) => {
  const { state } = useSensorsContext();
  const { searchAndConnect, deleteAnnotation } = useSensors(isHubFlow);
  const { recordings } = useActiveSessionStore();

  useEffect(() => {
    if (state.sensorsInDatabase.length > 0) {
      searchAndConnect(state.sensorsInDatabase.map((sensor) => sensor.id));
    }
  }, [state.sensorsInDatabase]);

  const wrappedOnComplete = (
    event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>,
    callback?: MouseEventHandler<HTMLIonButtonElement>,
  ) => {
    const activeRecording = recordings.at(-1);

    if (activeRecording) {
      deleteAnnotation(activeRecording.count, activeRecording.movementType);
    }

    callback?.(event);
  };

  return (
    <div className={styles.container}>
      <IonImg
        alt='Stylized cross made of six red dots in a curved, upward motion, centered within a large off-white circle.'
        className={styles.illustration}
        src={ErrorIllustration}
      />
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.description}>{description}</p>

      <div className={styles.buttons}>
        <div className={styles.topButtons}>
          <Button
            className={styles.button}
            variant='secondary'
            {...endSessionButton}
            onClick={(event) => wrappedOnComplete(event, endSessionButton?.onClick)}>
            {endSessionButton?.children ?? 'End session'}
          </Button>

          <Button
            className={styles.button}
            disabled
            variant='primary'
            {...newRecordingButton}
            onClick={(event) => wrappedOnComplete(event, newRecordingButton?.onClick)}>
            {newRecordingButton?.children ?? 'New recording'}
          </Button>
        </div>

        <div className={styles.divider}>
          <hr />
          <span>OR</span>
          <hr />
        </div>

        <Button
          className={styles.button}
          variant='secondary'
          {...reconfigureSensorsButton}
          onClick={(event) => wrappedOnComplete(event)}>
          {reconfigureSensorsButton?.children ?? 'Re-Configure Sensors'}
        </Button>
      </div>
    </div>
  );
};
